import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";
import "./Toast.scss";

// Fontaine M33 — feedback system replacing react-alert. ToastProvider + useToast() (queue, tones, actions
// like Undo, auto-dismiss, reduced-motion) and the inline Alert banner for page-level states.
// Position: bottom-left on laptop, above the tab bar on phones (V2 `toast`).

const ToastCtx = createContext(null);
const ICONS = { success: "CheckCircle2", error: "AlertTriangle", warning: "AlertTriangle", info: "Info" };
const DEFAULT_MS = { success: 4000, info: 5000, warning: 6000, error: 8000 };
let seq = 0;

export function ToastProvider({ children, max = 3 }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
  }, []);

  const push = useCallback((opts) => {
    const o = typeof opts === "string" ? { title: opts } : opts || {};
    const id = o.id ?? `t${++seq}`;
    const tone = o.tone || "info";
    const duration = o.duration ?? (o.action ? 8000 : DEFAULT_MS[tone]);
    setToasts((t) => [...t.filter((x) => x.id !== id), { ...o, id, tone }].slice(-max));
    clearTimeout(timers.current.get(id));
    if (duration > 0) timers.current.set(id, setTimeout(() => dismiss(id), duration));
    return id;
  }, [dismiss, max]);

  useEffect(() => () => timers.current.forEach((t) => clearTimeout(t)), []);

  const api = useMemo(() => {
    const fn = (opts) => push(opts);
    fn.success = (title, o) => push({ ...o, title, tone: "success" });
    fn.error = (title, o) => push({ ...o, title, tone: "error" });
    fn.info = (title, o) => push({ ...o, title, tone: "info" });
    fn.warning = (title, o) => push({ ...o, title, tone: "warning" });
    fn.dismiss = dismiss;
    return fn;
  }, [push, dismiss]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {typeof document !== "undefined"
        ? createPortal(
            <div className="ft-toasts" role="region" aria-label="Notifications" aria-live="polite">
              {toasts.map((t) => (
                <div key={t.id} className={`ft-toast ft-toast--${t.tone}`} role={t.tone === "error" ? "alert" : "status"}>
                  <span className="ft-toast__icon" aria-hidden="true"><Icon name={ICONS[t.tone] || "Info"} size="sm" /></span>
                  <div className="ft-toast__text">
                    {t.title ? <strong className="ft-toast__title">{t.title}</strong> : null}
                    {t.description ? <span className="ft-toast__desc">{t.description}</span> : null}
                  </div>
                  {t.action ? (
                    <button type="button" className="ft-toast__action" onClick={() => { t.action.onClick?.(); dismiss(t.id); }}>{t.action.label}</button>
                  ) : null}
                  <button type="button" className="ft-toast__close" aria-label="Dismiss" onClick={() => dismiss(t.id)}><Icon name="X" size="xs" /></button>
                </div>
              ))}
            </div>,
            document.body
          )
        : null}
    </ToastCtx.Provider>
  );
}

/** useToast() → toast("Saved") · toast.success("Listing published", { action: { label: "Undo", onClick } }) */
export function useToast() {
  const api = useContext(ToastCtx);
  if (!api) {
    // No provider (tests, isolated renders): log instead of throwing so a page never dies for a toast.
    const noop = () => { if (import.meta.env?.DEV) console.warn("useToast: no ToastProvider mounted"); return null; };
    noop.success = noop; noop.error = noop; noop.info = noop; noop.warning = noop; noop.dismiss = () => {};
    return noop;
  }
  return api;
}

/**
 * react-alert compatible shim: `const alert = useAlert(); alert.show("Saved")` keeps working while the
 * 38 call sites migrate to useToast(). show(msg, { type }) maps type → tone.
 */
export function useAlert() {
  const toast = useToast();
  return useMemo(() => ({
    show: (message, opts = {}) => toast({ title: message, tone: opts.type === "error" ? "error" : opts.type === "success" ? "success" : opts.type || "info", duration: opts.timeout }),
    success: (message, opts = {}) => toast.success(message, { duration: opts.timeout }),
    error: (message, opts = {}) => toast.error(message, { duration: opts.timeout }),
    info: (message, opts = {}) => toast.info(message, { duration: opts.timeout }),
    remove: (id) => toast.dismiss(id),
  }), [toast]);
}

/**
 * Alert — inline banner for page-level states (V2 `alert-info / alert-error / alert-success`).
 * @param {"info"|"success"|"warning"|"error"} [tone="info"]
 * @param {() => void} [onDismiss]
 * @param {{label: string, onClick: () => void}} [action]
 */
export function Alert({ tone = "info", title, children, action, onDismiss, icon = true, className = "", ...rest }) {
  return (
    <div className={`ft-alert ft-alert--${tone} ${className}`.trim()} role={tone === "error" ? "alert" : "status"} {...rest}>
      {icon ? <span className="ft-alert__icon" aria-hidden="true"><Icon name={ICONS[tone] || "Info"} size="sm" /></span> : null}
      <div className="ft-alert__text">
        {title ? <strong className="ft-alert__title">{title}</strong> : null}
        {children ? <div className="ft-alert__body">{children}</div> : null}
      </div>
      {action ? <button type="button" className="ft-alert__action" onClick={action.onClick}>{action.label}</button> : null}
      {onDismiss ? <button type="button" className="ft-alert__close" aria-label="Dismiss" onClick={onDismiss}><Icon name="X" size="xs" /></button> : null}
    </div>
  );
}
